import React, { useMemo } from 'react';
import { StyleSheet, ViewStyle, View, Text } from 'react-native';

import Assets from '../../App/assets';
import { AssetImage } from '../../App/assets/AssetImage';
import { useLinearProgressUIHandler } from '../services/hooks';
import {
  Colors,
  Durations,
  Fonts,
  Sizes,
} from '../services/utils/AppConstants';
import {
  Animated,
  useAnimatedStyle,
  Easing,
  withTiming,
  EasingFunction,
  EasingFunctionFactory,
} from '../services/utils/polyfills';

type Props = {
  backgroundStyle?: ViewStyle;
  style?: ViewStyle;

  // 0 to 100
  progressPercentage: number;
  easing?: EasingFunction | EasingFunctionFactory;

  // 0 to 100
  targetPercentage?: number;
  target?: number;

  // 0 to 100
  lastExamScorePercentage?: number;
};

export const CommonLinearProgress = (props: Props): React.ReactElement => {
  const percent = useMemo(
    () => Math.min(100, Math.max(0, props.progressPercentage || 0)),
    [props.progressPercentage],
  );

  const lastExamPercent = useMemo(
    () => Math.min(100, Math.max(0, props.lastExamScorePercentage || 0)),
    [props.lastExamScorePercentage],
  );

  const shouldShowTarget = useMemo(() => {
    return props.targetPercentage !== undefined && props.targetPercentage >= 0;
  }, [props.targetPercentage]);

  const shouldShowBoost = useMemo(() => {
    return (
      props.lastExamScorePercentage !== undefined &&
      props.lastExamScorePercentage >= 0
    );
  }, [props.lastExamScorePercentage]);

  const targetPercent = useMemo(() => {
    return Math.min(100, Math.max(0, props.targetPercentage || 0));
  }, [props.targetPercentage]);

  const wrapperStyle = useMemo(() => {
    if (!shouldShowTarget) {
      return styles.wrapper;
    }

    return [styles.wrapper, { paddingTop: Sizes.small, paddingBottom: 28 }];
  }, [shouldShowTarget]);

  const progressStyle = useAnimatedStyle(() => {
    const progressWidth = shouldShowTarget ? lastExamPercent : percent;
    return {
      width: withTiming(`${progressWidth}%`, {
        duration: Durations.quickTransition,
        easing: props.easing ?? Easing.inOut(Easing.quad),
      }),
    };
  }, [percent, lastExamPercent, props.easing]);

  const boostStyle = useAnimatedStyle(() => {
    const boostWidth = Math.max(0, percent - lastExamPercent);
    return {
      width: withTiming(`${boostWidth}%`, {
        duration: Durations.quickTransition,
        easing: Easing.inOut(Easing.quad),
      }),
      backgroundColor: Colors.sliceLightGreen,
      left: `${lastExamPercent - 1}%`,
    };
  }, [percent, lastExamPercent]);

  const {
    progressRef,
    targetContainerStyle,
    handleTargetLineLayout,
    handleTargetLayout,
  } = useLinearProgressUIHandler({ targetPercentage: targetPercent });

  return (
    <View style={wrapperStyle}>
      <View style={styles.mainContainer}>
        <View
          ref={progressRef as React.MutableRefObject<View>}
          style={[styles.progrssContainer, props.backgroundStyle]}>
          <Animated.View
            style={[styles.progress, props.style, progressStyle]}
          />
          {shouldShowBoost && (
            <Animated.View style={[styles.progress, props.style, boostStyle]} />
          )}
        </View>

        {shouldShowTarget && (
          <>
            <View
              style={[styles.targetLine, { left: `${targetPercent}%` }]}
              onLayout={handleTargetLineLayout}
            />

            <View
              onLayout={handleTargetLayout}
              style={[styles.targetContainer, targetContainerStyle]}>
              <AssetImage
                asset={Assets.test.bullseyeArrow}
                height={Sizes.semiMedium}
                fill={Colors.targetScoreTextColor}
              />
              <Text style={styles.targetText}>{props.target}</Text>
            </View>
          </>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    width: '100%',
  },
  mainContainer: {
    width: '100%',
    overflow: 'visible',
  },
  progrssContainer: {
    width: '100%',
    backgroundColor: Colors.white12,
    height: Sizes.small,
    borderRadius: Sizes.small,
    overflow: 'hidden',
  },
  progress: {
    backgroundColor: Colors.emeraldGreen,
    borderRadius: Sizes.small,
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
  },

  targetLine: {
    width: 1,
    height: 20,
    backgroundColor: Colors.mediumGray2,
    position: 'absolute',
    top: -5,
  },

  targetContainer: {
    position: 'absolute',
    top: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: Sizes.small,
    paddingHorizontal: 6,
    gap: Sizes.xsmall,
    paddingVertical: Sizes.xsmall,
    backgroundColor: Colors.sliceLightGreen,
    transform: [{ translateY: 5 }, { translateX: -30 }],
  },

  targetText: {
    ...Fonts.smallBold,
    color: Colors.targetScoreTextColor,
  },
});
